import { useEffect, useRef, useState } from "react";
import { Typography, Box, Divider, IconButton, Slider } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import Replay10Icon from "@mui/icons-material/Replay10";
import Forward10Icon from "@mui/icons-material/Forward10";
import StarIcon from "@mui/icons-material/Star";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import NotesIcon from "@mui/icons-material/Notes";
import MicIcon from "@mui/icons-material/Mic";
import { BASE_URL } from "../../../constants/apiConstants";
import { useTranslation } from "react-i18next";

// score / mistakes both use the same 0-4 red · 5-7 orange · 8-10 green scale
const scoreColor = (value) => {
  if (value === null || value === undefined) return null;
  if (value <= 4) return { bg: "#FDECEC", fg: "#E53935" };
  if (value <= 7) return { bg: "#FFF3E0", fg: "#F59E0B" };
  return { bg: "#E8F5E9", fg: "#43A047" };
};

const ScorePill = ({ value }) => {
  const colors = scoreColor(value);
  if (!colors) {
    return (
      <Box
        sx={{
          backgroundColor: "#F5F5F5",
          color: "text.secondary",
          fontWeight: 700,
          fontSize: 15,
          borderRadius: 5,
          px: 1.75,
          py: 0.5,
          lineHeight: 1.6,
        }}
      >
        — / 10
      </Box>
    );
  }
  return (
    <Box
      sx={{
        backgroundColor: colors.bg,
        color: colors.fg,
        fontWeight: 700,
        fontSize: 15,
        borderRadius: 5,
        px: 1.75,
        py: 0.5,
        lineHeight: 1.6,
      }}
    >
      {value} / 10
    </Box>
  );
};
const AssessmentStatRow = ({ icon, iconColor, label, value }) => {
  const colors = scoreColor(value);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 1.25,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ color: iconColor, display: "flex", fontSize: 20 }}>{icon}</Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Box
        sx={{
          backgroundColor: colors?.bg ?? "#F5F5F5",
          color: colors?.fg ?? "text.secondary",
          fontWeight: 700,
          fontSize: 13,
          minWidth: 28,
          textAlign: "center",
          borderRadius: 5,
          px: 1,
          py: 0.25,
        }}
      >
        {value ?? 0}
      </Box>
    </Box>
  );
};

const RangeChip = ({ label, color, children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      backgroundColor: color.bg,
      color: color.fg,
      fontWeight: 600,
      fontSize: 12,
      borderRadius: 4,
      px: 1,
      py: 0.25,
      mr: 1,
    }}
  >
    {label}
  </Box>
);

// Picks the right localized name field (e.g. sura_from_en / sura_from_ar / sura_from_tr)
// based on the current i18n language, falling back to the raw id if no name is present.
const langSuffix = (lng) => {
  if (!lng) return "en";
  const base = lng.split("-")[0].toLowerCase();
  if (["en", "ar", "tr"].includes(base)) return base;
  return "en";
};

const FromToRow = ({ details }) => {
  const { t, i18n } = useTranslation();
  const suffix = langSuffix(i18n.language);

  const nameOrId = (prefix, idValue) =>
    details[`${prefix}_${suffix}`] ?? details[`${prefix}_en`] ?? idValue;

  const fromSura = nameOrId("sura_from", details.sura_from);
  const fromJuz = nameOrId("juz_from", details.juz_from);
  const toSura = nameOrId("sura_to", details.sura_to);
  const toJuz = nameOrId("juz_to", details.juz_to);

  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", mb: 1 }}>
      <Box sx={{ color: "#7E57C2", display: "flex", fontSize: 20, mt: 0.25 }}>
        <MenuBookIcon fontSize="inherit" />
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
          <RangeChip label={t("studentDetails.activityCard.from")} color={{ bg: "#EDE9FE", fg: "#6D28D9" }} />
          <Typography variant="body2" color="text.secondary">
            {t("studentDetails.activityCard.juz")} {fromJuz} • {" "}
            {fromSura} • {t("studentDetails.activityCard.aya")} {details.aya_from}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
          <RangeChip label={t("studentDetails.activityCard.to")} color={{ bg: "#D1FAE5", fg: "#047857" }} />
          <Typography variant="body2" color="text.secondary">
            {t("studentDetails.activityCard.juz")} {toJuz} •{" "}
            {toSura} • {t("studentDetails.activityCard.aya")} {details.aya_to}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds)) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const AssessmentAudioPlayer = ({ src }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onLoadedMetadata = () => setDuration(audio.duration || 0);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onEnded = () => setIsPlaying(false);
    const onError = () => setHasError(true);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    return () => {
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
  }, [src]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const skip = (delta) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.min(Math.max(0, audio.currentTime + delta), duration || audio.currentTime + delta);
  };

  const handleSeek = (_, value) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = value;
    setCurrentTime(value);
  };

  const { t } = useTranslation();

  return (
    <Box
      sx={{
        backgroundColor: "#F8F1FB",
        border: "1px solid #EBDCF3",
        borderRadius: 3,
        px: 2,
        py: 1.5,
        mt: 1.5,
      }}
    >
      <audio ref={audioRef} src={src} preload="metadata" />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <MicIcon sx={{ color: "#8E24AA", fontSize: 20 }} />
        <Typography variant="body2" fontWeight={700} sx={{ color: "#8E24AA" }}>
          {t("studentDetails.activityCard.assessmentRecording")}
        </Typography>
      </Box>

      {hasError ? (
        <Typography variant="body2" color="text.secondary">
          {t("studentDetails.activityCard.audioUnavailable")}
        </Typography>
      ) : (
        <>
          <Slider
            size="small"
            value={currentTime}
            max={duration || 0}
            onChange={handleSeek}
            disabled={!duration}
            sx={{
              color: "#8E24AA",
              py: 0.5,
              "& .MuiSlider-thumb": { width: 14, height: 14 },
            }}
          />

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36 }}>
              {formatTime(currentTime)}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton size="small" onClick={() => skip(-10)} disabled={!duration} sx={{ color: "#8E24AA" }}>
                <Replay10Icon />
              </IconButton>
              <IconButton
                onClick={togglePlay}
                disabled={!duration}
                sx={{
                  backgroundColor: "#8E24AA",
                  color: "#fff",
                  width: 40,
                  height: 40,
                  "&:hover": { backgroundColor: "#7B1FA2" },
                  "&.Mui-disabled": { backgroundColor: "#D8C2E0", color: "#fff" },
                }}
              >
                {isPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
              </IconButton>
              <IconButton size="small" onClick={() => skip(10)} disabled={!duration} sx={{ color: "#8E24AA" }}>
                <Forward10Icon />
              </IconButton>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36, textAlign: "right" }}>
              {formatTime(duration)}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

const QuranAssessmentCard = ({ details }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        backgroundColor: "transparent",
        width: "100%",
        minWidth: 0,
      }}
    >
      {/* Score */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <StarIcon sx={{ color: "#F59E0B", fontSize: 22 }} />
          <Typography variant="body1" fontWeight={700}>
            {t("studentDetails.activityCard.assessmentScore")}:
          </Typography>
        </Box>
        <ScorePill value={details.score} />
      </Box>

      {/* Mistake stats */}
      <AssessmentStatRow
        icon={<RecordVoiceOverIcon fontSize="inherit" />}
        iconColor={scoreColor(details.mistakes_telawa)?.fg ?? "text.secondary"}
        label={t("studentDetails.activityCard.memorizationMistakes")}
        value={details.mistakes_telawa}
      />
      <AssessmentStatRow
        icon={<AutoStoriesIcon fontSize="inherit" />}
        iconColor={scoreColor(details.mistakes_tagweed)?.fg ?? "text.secondary"}
        label={t("studentDetails.activityCard.tajweedMistakes")}
        value={details.mistakes_tagweed}
      />

      {/* From / To */}
      <FromToRow details={details} />

      {/* Note */}
      {details.note && (
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", mb: 0.5, minWidth: 0 }}>
          <Box sx={{ color: "text.secondary", display: "flex", fontSize: 20, flexShrink: 0 }}>
            <NotesIcon fontSize="inherit" />
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              minWidth: 0,
              flex: 1,
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            }}
          >
            <strong>{t("studentDetails.activityCard.quranassessmentnote")}:</strong> {details.note}
          </Typography>
        </Box>
      )}

      {/* Audio */}
      {details.audio && <AssessmentAudioPlayer src={BASE_URL + details.audio} />}
    </Box>
  );
};

export default QuranAssessmentCard;